import React, { Component, ErrorInfo, ReactNode } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, RefreshCcw, Home } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const springConfig = {
  type: "spring",
  stiffness: 50,
  damping: 20,
  mass: 1
};

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Log error to your error tracking service
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center p-4" role="alert">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={springConfig}
            className="max-w-md w-full text-center"
          >
            <motion.div
              animate={{
                scale: [1, 1.1, 1],
                rotate: [0, 10, -10, 0],
              }}
              transition={{
                duration: 1,
                repeat: Infinity,
                repeatType: "reverse"
              }}
              className="inline-block mb-6 text-amber-400"
            >
              <AlertTriangle className="w-16 h-16" aria-hidden="true" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-white mb-4">Something went wrong</h1>
            <p className="text-gray-400 mb-6">
              We apologize for the inconvenience. Our team has been notified and is working on the issue.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.button
                onClick={this.handleReload}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                transition={springConfig}
                className="px-6 py-3 bg-gradient-to-r from-amber-400 to-amber-600 rounded-lg text-black font-semibold inline-flex items-center gap-2"
                aria-label="Reload page"
              >
                <RefreshCcw className="w-5 h-5" aria-hidden="true" />
                Reload Page
              </motion.button>

              <Link to="/">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={springConfig}
                  className="px-6 py-3 border-2 border-amber-400 text-amber-400 rounded-lg font-semibold inline-flex items-center gap-2"
                  aria-label="Return to home page"
                >
                  <Home className="w-5 h-5" aria-hidden="true" />
                  Return Home
                </motion.button>
              </Link>
            </div>

            {process.env.NODE_ENV === 'development' && this.state.error && (
              <div className="mt-8 text-left">
                <details className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <summary className="text-red-400 cursor-pointer">Error Details</summary>
                  <pre className="mt-4 text-sm text-red-300 overflow-auto">
                    {this.state.error.toString()}
                    {this.state.errorInfo?.componentStack}
                  </pre>
                </details>
              </div>
            )}
          </motion.div>
        </div>
      );
    }

    return this.props.children;
  }
}